import {
  forwardRef,
  HTMLProps,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import ReactDOM from 'react-dom';
import useContainerWidth from 'hooks/useContainerWidth';
import useFilteredItemsByToken from 'hooks/useFilteredItemsByToken';

export type SelectProps = Pick<
  HTMLProps<HTMLInputElement>,
  'onBlur' | 'name' | 'placeholder' | 'disabled' | 'readOnly' | 'className'
> & {
  items: DataItem[];
  value?: DataItem;
  onChange(v?: DataItem): void;
  showAllOption?: boolean;
  allOptionLabel?: string;
  hasError?: boolean;
};

const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      className,
      items,
      value,
      onChange,
      showAllOption,
      allOptionLabel = 'All',
      readOnly = false,
      placeholder,
      disabled,
      onBlur,
      hasError,
      ...props
    },
    ref,
  ) => {
    const tooltipId = useId();
    const selectorId = `selector-${tooltipId}`;

    const [isOpened, setIsOpened] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const highlightedButtonRef = useRef<HTMLButtonElement>(null);

    const [filteredItems, searchToken, setSearchToken] = useFilteredItemsByToken(items);

    const containerWidth = useContainerWidth(selectorId, [isOpened, items, value]);

    useLayoutEffect(() => {
      if (!isOpened || !isFocused) {
        setSearchToken('');
      }
    }, [isOpened, isFocused, setSearchToken]);

    // Auto-scroll to highlighted item
    useEffect(() => {
      if (highlightedIndex >= 0 && highlightedButtonRef.current && dropdownRef.current) {
        highlightedButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, [highlightedIndex]);

    const callbacks = useMemo(
      () => ({
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchToken(e.target.value),
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          setIsFocused(false);
          e.preventDefault();
          onBlur?.(e);
        },
        onFocus: () => setIsFocused(true),
      }),
      [onBlur, setSearchToken],
    );

    const closeMenu = useCallback(() => {
      setIsOpened(false);
      setHighlightedIndex(-1);
      document.getElementById(selectorId)?.blur();
    }, [selectorId]);

    const selectItem = useCallback(
      (item?: DataItem) => {
        onChange(item);
        closeMenu();
      },
      [onChange, closeMenu],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (!isOpened) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setIsOpened(true);
          }
          return;
        }

        const totalItems = (showAllOption ? 1 : 0) + filteredItems.length;

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
            break;
          case 'Enter':
            e.preventDefault();
            if (highlightedIndex === 0 && showAllOption) {
              selectItem(undefined);
            } else {
              const itemIndex = showAllOption ? highlightedIndex - 1 : highlightedIndex;
              if (itemIndex >= 0 && itemIndex < filteredItems.length) {
                selectItem(filteredItems[itemIndex]);
              }
            }
            break;
          case 'Escape':
            e.preventDefault();
            closeMenu();
            break;
          case 'Tab':
            closeMenu();
            break;
        }
      },
      [isOpened, showAllOption, filteredItems, highlightedIndex, selectItem, closeMenu],
    );

    const isSearchEnabled = !readOnly && isOpened;
    const _placeholder = placeholder || (readOnly ? 'Select option' : 'Type to search...');

    return (
      <>
        <input
          {...props}
          {...callbacks}
          onKeyDown={handleKeyDown}
          id={selectorId}
          data-tooltip-id={tooltipId}
          ref={ref}
          value={(isSearchEnabled ? searchToken : value?.label) || ''}
          placeholder={isSearchEnabled ? value?.label || _placeholder : _placeholder}
          className={clsx(
            'py-2 px-4 rounded border text-sm font-semibold',
            'with-dropdown-arrow pr-8 text-brand-black',
            showAllOption && 'placeholder:text-brand-black',
            hasError ? 'border-red-500 focus:ring-1 focus:ring-red-500 outline-0' : 'border-brand-purple-border',
            className,
          )}
          autoComplete={'off'}
          autoCorrect={'off'}
          readOnly={readOnly}
          disabled={disabled}
        />

        {!disabled &&
          ReactDOM.createPortal(
            <Tooltip
              id={tooltipId}
              clickable
              openOnClick
              noArrow
              offset={4}
              isOpen={isOpened}
              setIsOpen={setIsOpened}
              style={{ padding: 0, width: containerWidth }}
              className={'shadow-lg border border-brand-purple-border rounded-md z-[60]'}
              variant="light"
              place="bottom"
              opacity={1}
            >
              <div
                ref={dropdownRef}
                className={'block max-h-[128px] md:max-h-[192px] overflow-y-auto overflow-x-hidden'}
              >
                {showAllOption && (
                  <button
                    ref={highlightedIndex === 0 ? highlightedButtonRef : undefined}
                    className={clsx(
                      'text-sm font-semibold px-4 py-2 block w-full text-left text-brand-black break-words',
                      !value?.id
                        ? 'bg-brand-red-origin/10'
                        : highlightedIndex === 0
                          ? 'bg-brand-red-origin/20'
                          : 'hover:bg-brand-red-origin/10 active:bg-brand-red-origin/25',
                    )}
                    type="button"
                    onClick={() => {
                      selectItem(undefined);
                    }}
                  >
                    {allOptionLabel}
                  </button>
                )}

                {filteredItems.map((item, index) => {
                  const itemIndex = showAllOption ? index + 1 : index;
                  const isHighlighted = highlightedIndex === itemIndex;

                  return (
                    <button
                      ref={isHighlighted ? highlightedButtonRef : undefined}
                      className={clsx(
                        'text-sm font-semibold px-4 py-2 block w-full text-left text-brand-black break-words',
                        value?.id === item.id
                          ? 'bg-brand-red-origin/10'
                          : isHighlighted
                            ? 'bg-brand-red-origin/20'
                            : 'hover:bg-brand-red-origin/10 active:bg-brand-red-origin/25',
                      )}
                      type="button"
                      key={item.id}
                      onClick={() => {
                        selectItem(item);
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}

                {!showAllOption && filteredItems.length === 0 && (
                  <p className={'w-full text-center p-4 text-sm text-brand-black'} onClick={closeMenu}>
                    No options found
                  </p>
                )}
              </div>
            </Tooltip>,
            document.body,
          )}
      </>
    );
  },
);

Select.displayName = 'Select';

export default Select;
